/**
 * Contract Signing Functions
 * Manages digital signatures and contract finalization
 */

import {
  doc,
  setDoc,
  getDoc,
  collection,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  updateDoc,
  deleteField,
  type FieldValue,
} from 'firebase/firestore';
import { db } from './firebase';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Types
export interface Signature {
  userId: string;
  signatureData: string; // Base64 encoded signature image or typed name
  signatureType: 'drawn' | 'typed';
  signedAt: Timestamp;
  ipAddress?: string;
  userAgent?: string;
}

export interface SignedContract {
  relationshipId: string;
  contractVersion: string;
  contractContent: string; // HTML or PDF URL
  signatures: {
    partner1: Signature | null;
    partner2: Signature | null;
  };
  fullySignedAt: Timestamp | null;
  pdfUrl?: string; // Cloud Storage URL (future enhancement)
}

/**
 * Save a partner's signature to Firestore
 */
export async function saveSignature(
  relationshipId: string,
  userId: string,
  userName: string,
  signatureData: string,
  signatureType: 'drawn' | 'typed'
): Promise<{ success: boolean }> {
  try {
    // Get current signed contract or create new one
    const contractRef = doc(
      db,
      'relationships',
      relationshipId,
      'signedContracts',
      'current'
    );

    const contractSnap = await getDoc(contractRef);
    const existingData = contractSnap.exists() ? contractSnap.data() : null;

    // Get relationship to determine which partner
    const relationshipRef = doc(db, 'relationships', relationshipId);
    const relationshipSnap = await getDoc(relationshipRef);

    if (!relationshipSnap.exists()) {
      toast.error('Relationship not found');
      return { success: false };
    }

    const relationship = relationshipSnap.data();
    const isPartner1 = relationship.partner1Id === userId;
    const partnerKey = isPartner1 ? 'partner1' : 'partner2';

    // Create signature object
    const signature: Signature = {
      userId,
      signatureData,
      signatureType,
      signedAt: Timestamp.now(),
      userAgent: navigator.userAgent,
    };

    // Update the contract with new signature
    const signatures = existingData?.signatures || { partner1: null, partner2: null };
    signatures[partnerKey] = signature;

    // Check if both partners have signed
    const bothSigned = signatures.partner1 !== null && signatures.partner2 !== null;

    await setDoc(contractRef, {
      relationshipId,
      contractVersion: '1.0',
      contractContent: existingData?.contractContent || '',
      signatures,
      fullySignedAt: bothSigned ? serverTimestamp() : null,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    toast.success(`Signature saved successfully`);
    return { success: true };
  } catch (error) {
    console.error('Error saving signature:', error);
    toast.error('Failed to save signature');
    return { success: false };
  }
}

/**
 * Get the signed contract for a relationship
 */
export async function getSignedContract(
  relationshipId: string
): Promise<SignedContract | null> {
  try {
    const contractRef = doc(
      db,
      'relationships',
      relationshipId,
      'signedContracts',
      'current'
    );

    const contractSnap = await getDoc(contractRef);

    if (!contractSnap.exists()) {
      return null;
    }

    const data = contractSnap.data();
    return {
      relationshipId: data.relationshipId,
      contractVersion: data.contractVersion,
      contractContent: data.contractContent,
      signatures: data.signatures,
      fullySignedAt: data.fullySignedAt,
      pdfUrl: data.pdfUrl,
    };
  } catch (error) {
    console.error('Error getting signed contract:', error);
    toast.error('Failed to load contract');
    return null;
  }
}

/**
 * Check if both partners have signed the contract
 */
export async function isBothPartnersSigned(
  relationshipId: string
): Promise<boolean> {
  try {
    const contract = await getSignedContract(relationshipId);

    if (!contract) {
      return false;
    }

    return (
      contract.signatures.partner1 !== null &&
      contract.signatures.partner2 !== null
    );
  } catch (error) {
    console.error('Error checking signature status:', error);
    return false;
  }
}

/**
 * Subscribe to signing status changes
 */
export function subscribeToSigningStatus(
  relationshipId: string,
  callback: (contract: SignedContract | null) => void
): () => void {
  const contractRef = doc(
    db,
    'relationships',
    relationshipId,
    'signedContracts',
    'current'
  );

  return onSnapshot(
    contractRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.data();
      callback({
        relationshipId: data.relationshipId,
        contractVersion: data.contractVersion,
        contractContent: data.contractContent,
        signatures: data.signatures,
        fullySignedAt: data.fullySignedAt,
        pdfUrl: data.pdfUrl,
      });
    },
    (error) => {
      console.error('Error subscribing to signing status:', error);
      toast.error('Failed to sync signature status');
    }
  );
}

/**
 * Generate PDF from contract HTML with signatures
 */
export async function generateSignedPDF(
  relationshipId: string,
  contractElement: HTMLElement
): Promise<{ success: boolean; blob?: Blob }> {
  try {
    toast.info('Generating PDF...');

    // Use html2canvas to capture the contract element
    const canvas = await html2canvas(contractElement, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    // Calculate PDF dimensions
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');
    let position = 0;

    // Add image to PDF (with pagination if needed)
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Convert to blob
    const blob = pdf.output('blob');

    toast.success('PDF generated successfully');
    return { success: true, blob };
  } catch (error) {
    console.error('Error generating PDF:', error);
    toast.error('Failed to generate PDF');
    return { success: false };
  }
}

/**
 * Download the signed contract as PDF
 */
export async function downloadSignedPDF(
  relationshipId: string,
  contractElement: HTMLElement,
  partner1Name: string,
  partner2Name: string
): Promise<void> {
  try {
    const result = await generateSignedPDF(relationshipId, contractElement);

    if (result.success && result.blob) {
      // Create download link
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `love-contract-${partner1Name}-${partner2Name}-signed.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    }
  } catch (error) {
    console.error('Error downloading PDF:', error);
    toast.error('Failed to download PDF');
  }
}

/**
 * Finalize the contract after both partners sign
 */
export async function finalizeContract(
  relationshipId: string
): Promise<{ success: boolean }> {
  try {
    const bothSigned = await isBothPartnersSigned(relationshipId);

    if (!bothSigned) {
      toast.error('Both partners must sign before finalizing');
      return { success: false };
    }

    const contractRef = doc(
      db,
      'relationships',
      relationshipId,
      'signedContracts',
      'current'
    );

    await updateDoc(contractRef, {
      finalized: true,
      finalizedAt: serverTimestamp(),
    });

    // Update relationship status
    const relationshipRef = doc(db, 'relationships', relationshipId);
    await updateDoc(relationshipRef, {
      contractFinalized: true,
      contractFinalizedAt: serverTimestamp(),
    });

    toast.success('Contract finalized successfully');
    return { success: true };
  } catch (error) {
    console.error('Error finalizing contract:', error);
    toast.error('Failed to finalize contract');
    return { success: false };
  }
}

/**
 * Get signing status for display
 */
export async function getSigningStatus(
  relationshipId: string
): Promise<{
  partner1Signed: boolean;
  partner2Signed: boolean;
  partner1SignedAt: Timestamp | null;
  partner2SignedAt: Timestamp | null;
  bothSigned: boolean;
  fullySignedAt: Timestamp | null;
}> {
  try {
    const contract = await getSignedContract(relationshipId);

    if (!contract) {
      return {
        partner1Signed: false,
        partner2Signed: false,
        partner1SignedAt: null,
        partner2SignedAt: null,
        bothSigned: false,
        fullySignedAt: null,
      };
    }

    return {
      partner1Signed: contract.signatures.partner1 !== null,
      partner2Signed: contract.signatures.partner2 !== null,
      partner1SignedAt: contract.signatures.partner1?.signedAt || null,
      partner2SignedAt: contract.signatures.partner2?.signedAt || null,
      bothSigned: contract.signatures.partner1 !== null && contract.signatures.partner2 !== null,
      fullySignedAt: contract.fullySignedAt,
    };
  } catch (error) {
    console.error('Error getting signing status:', error);
    return {
      partner1Signed: false,
      partner2Signed: false,
      partner1SignedAt: null,
      partner2SignedAt: null,
      bothSigned: false,
      fullySignedAt: null,
    };
  }
}

/**
 * Remove signature (before finalization)
 */
export async function removeSignature(
  relationshipId: string,
  userId: string
): Promise<{ success: boolean }> {
  try {
    const contractRef = doc(
      db,
      'relationships',
      relationshipId,
      'signedContracts',
      'current'
    );

    const contractSnap = await getDoc(contractRef);

    if (!contractSnap.exists()) {
      toast.error('No contract found');
      return { success: false };
    }

    const contract = contractSnap.data();

    // Check if already finalized
    if (contract.finalized) {
      toast.error('Cannot remove signature from finalized contract');
      return { success: false };
    }

    // Get relationship to determine which partner
    const relationshipRef = doc(db, 'relationships', relationshipId);
    const relationshipSnap = await getDoc(relationshipRef);

    if (!relationshipSnap.exists()) {
      toast.error('Relationship not found');
      return { success: false };
    }

    const relationship = relationshipSnap.data();
    const isPartner1 = relationship.partner1Id === userId;
    const partnerKey = isPartner1 ? 'partner1' : 'partner2';

    // Remove the signature by setting to null
    const updates: Record<string, FieldValue | null> = {
      [`signatures.${partnerKey}`]: null,
      updatedAt: serverTimestamp(),
    };

    // Only delete fullySignedAt if it exists
    if (contract.fullySignedAt) {
      updates.fullySignedAt = deleteField();
    }

    await updateDoc(contractRef, updates);

    toast.success('Signature removed');
    return { success: true };
  } catch (error) {
    console.error('Error removing signature:', error);
    toast.error('Failed to remove signature');
    return { success: false };
  }
}
